import {
  IQboConnectionInfo,
  IQboSelectableAccounts,
  QboAccountClassification,
  QboSyncCategory,
} from '../interfaces/qbo'
import { AGGREGATION_TYPES } from './client'

// Which account_activity types each sync category covers.
//
// Keyed off AGGREGATION_TYPES because those are the exact `type` values the
// account_activity view emits, so a row resolves to its category by reading one
// column — no join back to `fees` for any category.
//
// The split follows Dwight's own GL codes (FeeTypeCodeMapping in
// interfaces/loanServicing), not Atlas's internal fee taxonomy:
//
//   1000  collections    Check / Wire / Adjustment         -> Collections
//   4001  interest       Interest Fee + Unused Line Fee    -> Interest
//   1800  facility       Facility Fee                      -> FacilityFees
//   4003  other fees     Wire / Lockbox / Termination / Misc -> OtherFees
//   1401  pass-through   folded into OtherFees (see below)
//
// Pass-through *costs* — legal, appraisals and field exams Dwight paid on the
// client's behalf — carry their own internal code (1401) but deliberately share the
// OtherFees account here. The view merges them with Miscellaneous fees into a
// single 'Fee' type, so giving them their own category would mean joining `fees` on
// fee_id for that one category alone. Pass-through *wires* are an entirely
// different thing and belong to Fundings — see that entry.
export const QBO_SYNC_CATEGORY_ACTIVITY_TYPES: Record<QboSyncCategory, string[]> = {
  // Adjustments sit with collections, not fees. Dwight books them to its collections
  // account (code 1000), and that is what they are: of the 18 ever recorded in
  // production, 16 are collection corrections — bounced checks, chargebacks,
  // misapplied payments, a wire re-posted on a different day, a payoff. Posting
  // them against a fee account would expense a bounced check. Their amount is
  // already the signed loan effect (a bounced check is positive, a mis-posted
  // payment being moved in is negative), so they are NOT a customer payment for
  // direction, memo or customer-attribution purposes — see
  // QBO_CUSTOMER_PAYMENT_ACTIVITY_TYPES.
  [QboSyncCategory.Collections]: [
    AGGREGATION_TYPES.check,
    AGGREGATION_TYPES.wire,
    AGGREGATION_TYPES.adjustment,
  ],
  // 'Pass-Through' here is the pass_throughs table — cash wired to the client,
  // averaging ~$265k a time — and NOT fees.type = 'Pass-Through', which the view
  // reports as 'Fee' and which belongs to OtherFees. Both types in this category
  // land in the client's own bank account, which QuickBooks imports on its own, so
  // posting them as well would show every deposit twice. Hence off by default.
  [QboSyncCategory.Fundings]: [AGGREGATION_TYPES.funding, AGGREGATION_TYPES.passThroughs],
  // Unused Line Fee sits with interest because that is how Dwight books it (code
  // 4001, alongside Interest / MinimumInterest / ManualInterest / TermLoanInterest):
  // it is a charge on undrawn capacity, not a service fee. Term loan interest also
  // arrives here — the view folds TermLoanInterest into 'Interest Fee' from the
  // `fees` table, so it needs no separate handling.
  [QboSyncCategory.Interest]: [
    AGGREGATION_TYPES.interestAmountMonthly,
    AGGREGATION_TYPES.unusedLineFee,
  ],
  // Its own category because it is the one fee clients genuinely separate: most
  // capitalize it as a deferred financing cost or debt issuance cost and amortize it
  // over the facility term, rather than expensing it. Its picker therefore also
  // offers Asset accounts — see QBO_SYNC_CATEGORY_ACCOUNT_CLASSIFICATIONS.
  [QboSyncCategory.FacilityFees]: [AGGREGATION_TYPES.facilityFee],
  [QboSyncCategory.OtherFees]: [
    AGGREGATION_TYPES.fee,
    AGGREGATION_TYPES.wireFee,
    AGGREGATION_TYPES.lockboxFee,
    AGGREGATION_TYPES.terminationFee,
  ],
}

// The kinds of activity that are a customer paying down the line: a check or a wire
// landing in the lockbox. Narrower than the Collections CATEGORY, which also holds
// adjustments, and it is this list — not the category — that decides three things
// in the sync engine:
//
//   direction   the view stores a payment's amount positive but its loan effect is
//               the negation; an adjustment's amount is already the loan effect
//   memo        a payment is described by the paying customer; an adjustment by
//               Dwight and its own title, like every other loan-side entry
//   customer    only a payment's description is a debtor name that can be matched
//               to a QuickBooks customer; an adjustment's is free text
//
// Adjustments still post against the same clearing account, which is the whole
// point of putting them in the category.
export const QBO_CUSTOMER_PAYMENT_ACTIVITY_TYPES: string[] = [
  AGGREGATION_TYPES.check,
  AGGREGATION_TYPES.wire,
]

export const isQboCustomerPayment = (activityType: string): boolean =>
  QBO_CUSTOMER_PAYMENT_ACTIVITY_TYPES.includes(activityType)

// Every activity type this integration will ever post, flattened.
//
// The sync query filters to this ALLOWLIST rather than excluding known-bad types.
// The account_activity view passes unmapped term_loan_activity.type values straight
// through, and there is already a live 'Miscellaneous Fee' row that matches no
// AGGREGATION_TYPES entry at all. Under a subtractive filter that row — and every
// activity type added later — would reach a client's books with no setting
// governing it and no switch able to stop it.
export const QBO_SYNCABLE_ACTIVITY_TYPES: string[] = Object.values(
  QBO_SYNC_CATEGORY_ACTIVITY_TYPES,
).flat()

// activity type -> the category whose switch and account govern it.
export const QBO_ACTIVITY_TYPE_SYNC_CATEGORY: Record<string, QboSyncCategory> = Object.entries(
  QBO_SYNC_CATEGORY_ACTIVITY_TYPES,
).reduce<Record<string, QboSyncCategory>>((acc, [category, types]) => {
  for (const type of types) {
    acc[type] = category as QboSyncCategory
  }
  return acc
}, {})

// Deliberately NOT syncable, and not merely unmapped.
//
// Principal is a balance transfer from a term loan to the ABL line: it moves both
// balances against each other and has no P&L effect. We hold exactly one liability
// account per connection — the ABL line — and no term loan account, so any entry we
// posted would have to invent a debit side, and the only account available would be
// a fee account. That would expense a balance transfer.
//
// The sync query drops it via `term_loan_activity_id IS NULL`, which also drops
// term-loan fundings and term-loan facility fees for the same reason: those move the
// term loan balance, not the line the client selected. This list exists so the
// exclusion is stated somewhere a reader will find it.
export const QBO_EXCLUDED_ACTIVITY_TYPES: string[] = [AGGREGATION_TYPES.principal]

export const QBO_SYNC_CATEGORY_LABELS: Record<QboSyncCategory, string> = {
  [QboSyncCategory.Collections]: 'Collections',
  [QboSyncCategory.Fundings]: 'Fundings & pass-throughs',
  [QboSyncCategory.Interest]: 'Interest',
  [QboSyncCategory.FacilityFees]: 'Facility fees',
  [QboSyncCategory.OtherFees]: 'Other fees',
}

// Order the categories are presented in, matching the settings table top to bottom.
export const QBO_SYNC_CATEGORY_ORDER: QboSyncCategory[] = [
  QboSyncCategory.Collections,
  QboSyncCategory.Fundings,
  QboSyncCategory.Interest,
  QboSyncCategory.FacilityFees,
  QboSyncCategory.OtherFees,
]

// Which classifications each category's account picker offers.
//
// No fee category is ever Expense-only. Clients routinely book Dwight fees to a
// balance-sheet account paired with the loan, and a facility fee is usually
// capitalized — so restricting these to Expense would hide the account the client
// already uses. See QboAccountClassification.
export const QBO_SYNC_CATEGORY_ACCOUNT_CLASSIFICATIONS: Record<
  QboSyncCategory,
  QboAccountClassification[]
> = {
  // Collections are swept out of a clearing/lockbox account or an operating bank
  // account, so this stays Asset-only, unchanged from the original integration.
  [QboSyncCategory.Collections]: [QboAccountClassification.Asset],
  // The client's operating bank account, where Dwight's wire actually lands.
  [QboSyncCategory.Fundings]: [QboAccountClassification.Asset],
  [QboSyncCategory.Interest]: [
    QboAccountClassification.Expense,
    QboAccountClassification.Liability,
  ],
  [QboSyncCategory.FacilityFees]: [
    QboAccountClassification.Expense,
    QboAccountClassification.Asset,
    QboAccountClassification.Liability,
  ],
  [QboSyncCategory.OtherFees]: [
    QboAccountClassification.Expense,
    QboAccountClassification.Liability,
  ],
}

// Empty account snapshot, used as the fallback wherever a connection has no cached
// accounts yet. Shared because it previously existed as three separate literals
// (ListAccounts, RefreshCache, and the settings panel), so adding a classification
// meant finding all three — and missing one silently produced a picker with no
// options. Read-only by convention: callers only ever read from it.
export const QBO_EMPTY_SELECTABLE_ACCOUNTS: IQboSelectableAccounts = {
  assets: [],
  liabilities: [],
  expenses: [],
}

// Which connection fields hold each category's switch and account.
//
// This map exists for exactly one reason: Collections has no fields of its own. It
// uses qboClearingAccount*, which predates per-category settings and is the more
// accurate name for what that account is. Rather than rename a column on the one
// code path already posting real money — or smear the exception through the sync
// engine and the settings UI — it is confined here, so every caller gets a uniform
// category -> (switch, account) lookup.
//
// Typed against IQboConnectionInfo, and the server indexes its QboConnection entity
// with these same key unions. That means a rename on EITHER side fails to compile
// rather than silently resolving to undefined on a money-moving path.
export interface IQboSyncCategoryFields {
  enabled: keyof Pick<
    IQboConnectionInfo,
    | 'isCollectionsSyncEnabled'
    | 'isFundingsSyncEnabled'
    | 'isInterestSyncEnabled'
    | 'isFacilityFeesSyncEnabled'
    | 'isOtherFeesSyncEnabled'
  >
  accountId: keyof Pick<
    IQboConnectionInfo,
    | 'qboClearingAccountId'
    | 'qboFundingAccountId'
    | 'qboInterestAccountId'
    | 'qboFacilityFeeAccountId'
    | 'qboOtherFeesAccountId'
  >
  accountName: keyof Pick<
    IQboConnectionInfo,
    | 'qboClearingAccountName'
    | 'qboFundingAccountName'
    | 'qboInterestAccountName'
    | 'qboFacilityFeeAccountName'
    | 'qboOtherFeesAccountName'
  >
}

export const QBO_SYNC_CATEGORY_FIELDS: Record<QboSyncCategory, IQboSyncCategoryFields> = {
  [QboSyncCategory.Collections]: {
    enabled: 'isCollectionsSyncEnabled',
    accountId: 'qboClearingAccountId',
    accountName: 'qboClearingAccountName',
  },
  [QboSyncCategory.Fundings]: {
    enabled: 'isFundingsSyncEnabled',
    accountId: 'qboFundingAccountId',
    accountName: 'qboFundingAccountName',
  },
  [QboSyncCategory.Interest]: {
    enabled: 'isInterestSyncEnabled',
    accountId: 'qboInterestAccountId',
    accountName: 'qboInterestAccountName',
  },
  [QboSyncCategory.FacilityFees]: {
    enabled: 'isFacilityFeesSyncEnabled',
    accountId: 'qboFacilityFeeAccountId',
    accountName: 'qboFacilityFeeAccountName',
  },
  [QboSyncCategory.OtherFees]: {
    enabled: 'isOtherFeesSyncEnabled',
    accountId: 'qboOtherFeesAccountId',
    accountName: 'qboOtherFeesAccountName',
  },
}

// Which IQboSelectableAccounts list each classification lives in, so a category's
// picker can combine several classifications into one option list.
export const QBO_ACCOUNT_LIST_BY_CLASSIFICATION: Record<
  QboAccountClassification,
  keyof IQboSelectableAccounts
> = {
  [QboAccountClassification.Asset]: 'assets',
  [QboAccountClassification.Liability]: 'liabilities',
  [QboAccountClassification.Expense]: 'expenses',
}
